/*
 * Copyright (c) 2025. Orion Analytics. All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { Slider, Typography } from "@mui/material";

const BinomialTree = () => {
  const [S0, setS0] = useState(100);
  const [K, setK] = useState(100);
  const [T, setT] = useState(1);
  const [r, setR] = useState(0.05);
  const [sigma, setSigma] = useState(0.2);
  const [steps, setSteps] = useState(4);
  const [data, setData] = useState([]);
  const [csvData, setCsvData] = useState(null); // Store the blob here

  const generateTree = () => {
    const dt = T / steps;
    const u = Math.exp(sigma * Math.sqrt(dt));
    const d = 1 / u;
    const p = (Math.exp(r * dt) - d) / (u - d);

    let stockTree = [[S0]];
    for (let i = 1; i <= steps; i++) {
      const level = [stockTree[i - 1][0] * u];
      for (let j = 1; j <= i; j++) {
        level.push(stockTree[i - 1][j - 1] * d);
      }
      stockTree.push(level);
    }

    let optionTree = Array(steps + 1)
      .fill()
      .map(() => []);
    optionTree[steps] = stockTree[steps].map(s => Math.max(s - K, 0));
    for (let i = steps - 1; i >= 0; i--) {
      for (let j = 0; j <= i; j++) {
        const val = Math.exp(-r * dt) * (p * optionTree[i + 1][j] + (1 - p) * optionTree[i + 1][j + 1]);
        optionTree[i][j] = val;
      }
    }

      // 📦 CSV Generation
  let csv = `Step,Node,Stock Price,Option Value\n`;
  for (let i = 0; i <= steps; i++) {
    for (let j = 0; j <= i; j++) {
      csv += `${i},${j},${stockTree[i][j].toFixed(4)},${optionTree[i][j]?.toFixed(4) || ""}\n`;
    }
  }
  csv += `\nParameters,,,\n`;
  csv += `u,${u}\nd,${d}\np,${p}\nSteps,${steps}\nStrike,${K}\nVolatility,${sigma}\nMaturity,${T}\nRate,${r}\n`;

  const blob = new Blob([csv], { type: "text/csv" });
  setCsvData(URL.createObjectURL(blob)); // Store blob URL

    // Generate coordinates and lines
    const x = [], y = [], text = [], lineX = [], lineY = [];
    for (let i = 0; i <= steps; i++) {
      let xStart = -i / 2;
      for (let j = 0; j <= i; j++) {
        const xi = xStart + j;
        const yi = -i;
        // x.push(xi);
        // y.push(yi);
        x.push(-yi);  // New X: use -yi so the tree grows rightward
        y.push(xi);   // New Y: use xi to spread nodes vertically

        text.push(`S=${stockTree[i][j].toFixed(2)}<br>Opt=${optionTree[i][j].toFixed(2)}`);
        if (i < steps) {
          const xiUp = -(i + 1) / 2 + j;
          const xiDown = xiUp + 1;
          const yNext = -(i + 1);
          // lineX.push(xi, xiUp, null, xi, xiDown, null);
          // lineY.push(yi, yNext, null, yi, yNext, null);
          lineX.push(-yi, -yNext, null, -yi, -yNext, null);
          lineY.push(xi, xiUp, null, xi, xiDown, null);

        }
      }
    }

    const edgeLines = {
      x: lineX,
      y: lineY,
      mode: "lines",
      type: "scatter",
      line: { color: "gray" },
      hoverinfo: "none"
    };

    const nodes = {
      x,
      y,
      mode: "markers+text",
      type: "scatter",
      marker: { size: 10, color: "royalblue" },
      text,
      textposition: "top center",
      hoverinfo: "text"
    };

    setData([edgeLines, nodes]);
  };

  useEffect(() => {
    generateTree();
  }, [S0, K, T, r, sigma, steps]);



  return (
<div style={{height:"auto" }}>
    <div className="col-md-6 col-lg-12 wow fadeInUp" style={{ marginBottom: "20px",height:"auto" }} data-wow-delay="0.6s">
      <div className="service-item">
        <h3>💸 Binomial Option Tree
          <a href={csvData} download="binomial_option_tree.csv">
            <img
              src="https://img.icons8.com/color/48/microsoft-excel-2019--v1.png"
              alt="Export CSV"
              title="Export Binomial Tree to CSV"
              style={{
                width: "30px",
                height: "30px",
                cursor: "pointer",
                margin: "10px"
              }}
            />
          </a>

        </h3>

        <label >
          Stock Price: {S0}
          <input type="range" min={1} max={100} step={1} value={S0} onChange={(e) => setS0(Number(e.target.value))} />
        </label>
        <label>
          Strike Price: {K}
          <input type="range" min={1} max={130} step={1} value={K} onChange={(e) => setK(Number(e.target.value))} />
        </label>
        <label>
          Volatility: {sigma*100}%
          <input type="range" min={0} max={0.4} step={0.01} value={sigma} onChange={(e) => setSigma(Number(e.target.value))} />
        </label>
        <label>
          Time to Maturity (Years): {T}
          <input type="range" min={0} max={20} step={0.25} value={T} onChange={(e) => setT(Number(e.target.value))} />
        </label>
        <label>
          Steps: {steps}
          <input type="range" min={0} max={15} step={1} value={steps} onChange={(e) => setSteps(Number(e.target.value))} />
        </label>
        <br />

        <Plot
          data={data}
          layout={{
            margin: { t: 10 },
            xaxis: { visible: true },
            yaxis: { visible: false },
            plot_bgcolor: "#fff",
            showlegend: false,
            height: 600
          }}
          style={{ width: "100%", height: "700px", maxWidth: "1000px" }}
        />
      </div>
    </div>
  </div>
  );
};

const SliderControl = ({ label, min, max, step, value, onChange }) => (
  <div style={{ margin: "20px 0" }}>
    <Typography gutterBottom>{label}: {value}</Typography>
    <Slider
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e, val) => onChange(val)}
      valueLabelDisplay="auto"
    />
  </div>
);

export default BinomialTree;
